<script setup lang="ts">
import {computed, getCurrentInstance, HTMLAttributes} from 'vue'
import {cn} from '@/shared/lib/utils'
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {IconDefinition} from "@fortawesome/fontawesome-svg-core";
import {faSpinnerThird, faTimes} from "@fortawesome/pro-solid-svg-icons";

const props = withDefaults(defineProps<{
  type?: 'text' | 'number' | 'password' | 'time' | 'hidden'
  placeholder?: string
  loading?: boolean
  clearable?: boolean
  icon?: IconDefinition
  iconPosition?: 'start' | 'end',
  iconClass?: HTMLAttributes['class']
  class?: HTMLAttributes['class']
  wrapperClass?: HTMLAttributes['class']
  appendClass?: HTMLAttributes['class']
  ref?: string
}>(), {
  type: 'text',
  iconPosition: "start",
  loading: false,
})

const emits = defineEmits<{
  (e: 'update:clear'): void
  (e: 'icon:click'): void
}>()
const modelValue = defineModel<string | number>()
const transformedModel = computed({
  get: () => modelValue.value,
  set: (value: string | number) => {
    if (props.type === 'number') {
      modelValue.value = Number(value)
    } else {
      modelValue.value = value
    }
  }
})

const isEventListenerDefined = (eventName: string) => {
  const instance = getCurrentInstance();
  if (instance && instance.vnode.props) {
    // Convert event names to the 'on'-prefixed equivalent (e.g., 'icon:click' => 'onIcon:click')
    const formattedEventName = 'on' + eventName.charAt(0).toUpperCase() + eventName.slice(1);
    return !!instance.vnode.props[formattedEventName];
  }
  return false;
};
const isIconClickDefined = computed(() => isEventListenerDefined('icon:click'));

const inputClass = computed(() => {
  const p = props.iconPosition == 'start' ? 'pl-10' : 'pr-10'
  return `flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 ${p} py-2 text-base md:text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50 read-only:bg-gray-100  dark:border-slate-800 dark:bg-slate-950 dark:ring-offset-slate-950 dark:placeholder:text-slate-400 dark:focus-visible:ring-slate-300`
})
</script>

<template>
  <div :class="cn('flex w-full justify-start', props.wrapperClass)">
    <div v-if="$slots.prepend"
         class="bg-slate-100 border border-slate-200 text-slate-500 text-sm rounded-l-md flex justify-center items-center px-2 h-10 min-w-10">
      <slot name="prepend"/>
    </div>
    <div v-if="icon" class="relative w-full items-center">
      <input :type="type" v-model="transformedModel" v-bind="$attrs" :placeholder="placeholder" data-lpignore="true" :ref="ref"
             :class="cn(inputClass, props.class)">
      <span
          @click="emits('icon:click')"
          :class="[
        iconPosition == 'start' ? 'start-1' : 'end-1',
        'absolute inset-y-0 flex items-center justify-center px-2',
        isIconClickDefined? 'cursor-pointer' : ''
      ]">
      <FontAwesomeIcon :icon="icon" :class="cn('text-muted-foreground', props.iconClass)"/>
    </span>
      <span v-if="loading" class="absolute inset-y-0 flex items-center justify-center px-2" :class="clearable ? 'end-6' : 'end-1'"
            data-tests="spinner">
      <FontAwesomeIcon :icon="faSpinnerThird" spin class="text-muted-foreground"/>
    </span>
    <span v-if="clearable && modelValue" class="absolute end-1 inset-y-0 flex items-center justify-center px-2" @click="emits('update:clear')">
      <FontAwesomeIcon :icon="faTimes" :class="cn('text-muted-foreground hover:text-foreground hover:cursor-pointer', props.iconClass)"/>
    </span>
    </div>
    <div v-else class="flex justify-between relative" :class="$slots.append ? '' : 'w-full'">
      <input :type="type" v-bind="$attrs" v-model="modelValue" :placeholder="placeholder" data-lpignore="true"
             :class="cn('flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-base md:text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50 read-only:bg-gray-100 dark:border-slate-800 dark:bg-slate-950 dark:ring-offset-slate-950 dark:placeholder:text-slate-400 dark:focus-visible:ring-slate-300', props.class, $slots.append ? 'rounded-r-none border-r-0' : '', $slots.prepend ? 'rounded-l-none border-l-0' : '')">
      <span v-if="loading" class="absolute inset-y-0 flex items-center justify-center px-2" :class="clearable ? 'end-6' : 'end-1'"
            data-tests="spinner">
      <FontAwesomeIcon :icon="faSpinnerThird" spin class="text-muted-foreground"/>
    </span>
      <span class="absolute end-1 inset-y-0 flex items-center justify-center px-2"
            :class="!clearable || (clearable && !modelValue) ? 'hidden' : 'flex'" @click="emits('update:clear')">
      <FontAwesomeIcon :icon="faTimes" :class="cn('text-muted-foreground hover:text-foreground hover:cursor-pointer', props.iconClass)"/>
    </span>
    </div>
    <div v-if="$slots.append"
         class="bg-slate-100 border border-slate-200 text-slate-500 text-sm rounded-r-md flex justify-center items-center px-default h-10 min-w-10 max-w-100 truncate"
         :class="cn($attrs.disabled == true ? 'cursor-not-allowed opacity-50 bg-gray-50' : '', props.appendClass)"
    >
      <slot name="append"/>
    </div>
  </div>
</template>
<style scoped>
input[type="number"] {
  position: relative;
}

/* Spin Buttons modified */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  background: #FFF url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAkAAAAJCAYAAADgkQYQAAAAKUlEQVQYlWNgwAT/sYhhKPiPT+F/LJgEsHv37v+EMGkmkuImoh2NoQAANlcun/q4OoYAAAAASUVORK5CYII=) no-repeat center center;
  width: 2em;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
}

input[type="number"]::-webkit-inner-spin-button:hover,
input[type="number"]::-webkit-inner-spin-button:active {
  opacity: 1;
}
</style>